/**
 * Shared utility for validating and sanitizing redirect paths
 * Used by both axios interceptor and login component to ensure consistent behavior
 */

/**
 * Validate and sanitize redirect paths to handle edge cases
 * @param {string} path - The path to validate
 * @param {object} router - Vue Router instance
 * @param {string} defaultPath - Default fallback path (optional)
 * @returns {string} - Validated and sanitized path
 */
export function validateRedirectPath(path, router, defaultPath = '/tasks') {
  // Handle null, undefined, or empty paths
  if (!path || typeof path !== 'string' || path.trim() === '') {
    return defaultPath;
  }
  
  // Remove any leading/trailing whitespace
  path = path.trim();
  
  // Ensure path starts with '/' (relative path only for security)
  if (!path.startsWith('/')) {
    return defaultPath;
  }
  
  // Prevent external URLs or protocol-relative URLs for security
  if (path.includes('://') || path.startsWith('//')) {
    return defaultPath;
  }
  
  // Decode URL-encoded characters
  try {
    path = decodeURIComponent(path);
  } catch (e) {
    // If decoding fails, return default path
    return defaultPath;
  }
  
  // Use Vue Router to dynamically validate if the route exists
  if (router) {
    try {
      const resolved = router.resolve(path);
      
      // Check if the route was successfully resolved and it's not a catch-all route
      if (resolved && resolved.route && resolved.route.matched.length > 0) {
        // Additional check: make sure it's not redirecting to a wildcard route
        const matchedRoute = resolved.route.matched[0];
        if (matchedRoute.path === '*' || matchedRoute.path === '/*') {
          return defaultPath;
        }
        return path;
      }
    } catch (e) {
      // If router resolution fails, fall back to default
      return defaultPath;
    }
  }
  
  // If route couldn't be resolved, return default path
  return defaultPath;
}
